import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { notification } from 'antd'
import { supabaseClient } from '@/configs/supabase'
import {
  getCustomerById,
  updateCustomerPricing,
  createCustomerPricing,
  ensureDefaultCustomer,
} from '@/supabase/Customer/customer'
import {
  ICustomerWithPricing,
  ICreateCustomerPricingInput,
  IUpdateCustomerPricingInput,
} from '@/supabase/Customer/customer.interface'
import { ICustomerPricingFormValues, DEFAULT_CUSTOMER_ID } from '../interfaces'

// Hook for fetching the default customer
export const useDefaultCustomer = () => {
  return useQuery({
    queryKey: ['customer', 'default'],
    queryFn: async () => {
      // Ensure default customer exists first
      await ensureDefaultCustomer(supabaseClient)

      // Then fetch it
      const customer = await getCustomerById(
        DEFAULT_CUSTOMER_ID,
        supabaseClient
      )
      return customer
    },
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: false,
    retry: 1,
  })
}

// Hook for updating default customer pricing
export const useUpdateDefaultCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (formData: ICustomerPricingFormValues) => {
      // Get current default customer to check if pricing exists
      const defaultCustomer = await getCustomerById(
        DEFAULT_CUSTOMER_ID,
        supabaseClient
      )

      if (!defaultCustomer) {
        throw new Error('Default customer not found')
      }

      const pricingData = {
        largeIceCubePriceTHB: formData.largeIceCubePriceTHB,
        smallIceCubePriceTHB: formData.smallIceCubePriceTHB,
        crushedIcePriceTHB: formData.crushedIcePriceTHB,
        isDefaultPricing: true,
      }

      let updatedPricing
      if (defaultCustomer.pricing) {
        // Update existing pricing
        const updateData: IUpdateCustomerPricingInput = {
          ...pricingData,
        }
        updatedPricing = await updateCustomerPricing(
          DEFAULT_CUSTOMER_ID,
          updateData,
          supabaseClient
        )
      } else {
        // Create new pricing
        const createData: ICreateCustomerPricingInput = {
          customerId: DEFAULT_CUSTOMER_ID,
          ...pricingData,
        }
        updatedPricing = await createCustomerPricing(createData, supabaseClient)
      }

      if (!updatedPricing) {
        throw new Error('Failed to update default pricing')
      }

      return {
        ...defaultCustomer,
        pricing: updatedPricing,
      } as ICustomerWithPricing
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', 'default'] })
      queryClient.invalidateQueries({ queryKey: ['customers', 'REGULAR'] })
      notification.success({
        message: 'Success',
        description: 'Default customer pricing updated successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message ||
        'Error occurred while updating default customer pricing'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}

// Hook to initialize default customer if it doesn't exist
export const useInitializeDefaultCustomer = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      // ensureDefaultCustomer now throws errors instead of returning boolean
      await ensureDefaultCustomer(supabaseClient)
      return true
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customer', 'default'] })
      notification.success({
        message: 'Success',
        description: 'Default customer created successfully',
        placement: 'topRight',
      })
    },
    onError: (error: any) => {
      const errorMessage =
        error?.message || 'Error occurred while creating default customer'
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      })
    },
  })
}
